import React from 'react';
import { Sparkles } from 'lucide-react';
import { ModuleAssessment } from '../../types/content';
import Button from '../UI/Button';
import Input from '../UI/Input';

const emptyOption = (order: number, isCorrect = false) => ({ text: '', isCorrect, displayOrder: order });

const AssessmentFlowEditor: React.FC<{
  assessment: ModuleAssessment;
  onChange: (next: ModuleAssessment) => void;
  onRemove: () => void;
  onAskAi: () => void;
}> = ({ assessment, onChange, onRemove, onAskAi }) => {
  const updateQuestion = (questionIndex: number, nextPrompt: string) => {
    onChange({
      ...assessment,
      questions: assessment.questions.map((question, index) => (
        index === questionIndex ? { ...question, prompt: nextPrompt } : question
      )),
    });
  };

  const updateOption = (questionIndex: number, optionIndex: number, patch: { text?: string; isCorrect?: boolean }) => {
    onChange({
      ...assessment,
      questions: assessment.questions.map((question, index) => index !== questionIndex ? question : {
        ...question,
        options: question.options.map((option, n) => n === optionIndex ? { ...option, ...patch } : option),
      }),
    });
  };

  return (
    <div className="space-y-4 rounded-2xl border border-gray-200 bg-white p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-gray-500">Assessment in this module</p>
          <h2 className="text-lg font-semibold text-gray-900">{assessment.title || 'Untitled assessment'}</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onAskAi}>
            <Sparkles className="h-4 w-4 mr-1" /> Ask AI for assessment
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={onRemove}>Remove assessment</Button>
        </div>
      </div>
      <Input id="assessment-title" label="Assessment title" value={assessment.title} onChange={(e) => onChange({ ...assessment, title: e.target.value })} />
      <label className="block text-sm font-medium text-gray-700">Description
        <textarea
          className="mt-1 w-full rounded-md border-gray-300"
          rows={2}
          value={assessment.description || ''}
          onChange={(e) => onChange({ ...assessment, description: e.target.value })}
        />
      </label>
      <Input
        id="assessment-pass"
        type="number"
        label="Passing score %"
        value={String(assessment.passingScore)}
        onChange={(e) => onChange({ ...assessment, passingScore: Number(e.target.value) })}
      />
      {(assessment.questions || []).map((question, questionIndex) => (
        <div key={question.id || questionIndex} className="rounded-lg border border-gray-200 p-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-medium text-gray-800">Question {questionIndex + 1}</p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onChange({
                ...assessment,
                questions: assessment.questions.filter((_, index) => index !== questionIndex),
              })}
            >
              Remove question
            </Button>
          </div>
          <Input
            id={`q-${questionIndex}`}
            label="Prompt"
            value={question.prompt}
            onChange={(e) => updateQuestion(questionIndex, e.target.value)}
          />
          {question.options.map((option, optionIndex) => (
            <div key={option.id || optionIndex} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!option.isCorrect}
                aria-label="Correct answer"
                onChange={(e) => updateOption(questionIndex, optionIndex, { isCorrect: e.target.checked })}
              />
              <input
                className="flex-1 rounded-md border-gray-300 text-sm"
                value={option.text}
                placeholder={`Option ${optionIndex + 1}`}
                onChange={(e) => updateOption(questionIndex, optionIndex, { text: e.target.value })}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onChange({
                  ...assessment,
                  questions: assessment.questions.map((item, index) => index !== questionIndex ? item : {
                    ...item,
                    options: item.options.filter((_, n) => n !== optionIndex),
                  }),
                })}
              >
                Remove
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onChange({
              ...assessment,
              questions: assessment.questions.map((item, index) => index !== questionIndex ? item : {
                ...item,
                options: [...item.options, emptyOption(item.options.length + 1)],
              }),
            })}
          >
            Add option
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => onChange({
          ...assessment,
          questions: [...(assessment.questions || []), {
            prompt: '',
            explanation: '',
            displayOrder: (assessment.questions || []).length + 1,
            options: [emptyOption(1, true), emptyOption(2)],
          }],
        })}
      >
        Add question
      </Button>
    </div>
  );
};

export default AssessmentFlowEditor;
